import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Layout from "../../../Component/Layout";
import Select from "react-select";
import axios from "axios";
import "../../../utils/axiosAuth"; // installs the Authorization header interceptor
import { toast, ToastContainer } from "react-toastify";
import { MASTER_API } from "../../../apiConfig";
import html2pdf from "html2pdf.js";
import sbeeLogo from "../../../assets/sbeelogo.png";
import InspectionAssignmentTab from "./Inspectionassignmenttab";
import InspectionTemplateTab from "./Inspectiontemplatetab";
import InspectionProductFormatTab from "./productformattab";

const toOpts = (arr) => (arr || []).map((v) => ({ value: String(v), label: String(v) }));
const optsToParam = (opts) => (opts || []).map((o) => o.value).join(",");

const API = `${MASTER_API}/inspection`;

function InspectionCharacteristics() {

  /* ── dropdowns ── */
  const [ddl, setDdl] = useState({
    productTypes: [], productCategories: [], subCategories: [],
    qualityCategories: [], cableTypes: [], constructions: [], inspectionTypes: [],
    codes: [], colours: [], cores: [], nominalCrossSections: [], standards: [], voltages: [],
    productCodes: [], testTypes: [],
    categoryToCableTypes: {}, cableTypeToCode: {},
    categoryToSubCategory: {},
    subCategoryToQualityCategory: {},
  });
  const [allProducts, setAllProducts] = useState([]);

  // inspection_test_type rows for the currently selected product type
  const [allInspTestTypes, setAllInspTestTypes] = useState([]); // full list from config
  const [filteredInspTypes, setFilteredInspTypes] = useState([]); // filtered by productType
  const [filteredTestTypes, setFilteredTestTypes] = useState([]); // test type names for characteristics

  /* ── tab ── */
  const [tableView, setTableView] = useState("template");

  /* ── Template tab ── */
  const [templateList, setTemplateList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editInspNo, setEditInspNo] = useState(null);
  const [formData, setFormData] = useState({ inspectionNo: "", productType: "", inspectionType: [] });
  const [qualityConfigurations, setQualityConfigurations] = useState([]);
  const [selectedCharacteristics, setSelectedCharacteristics] = useState({});
  const [activeViewTab, setActiveViewTab] = useState("particulars");
  // per-characteristic: { [charKey]: { testType: "", type: "", variable: "", isFormula: "No", sequence: null } }
  const [charMeta, setCharMeta] = useState({});

  /* ── Assignment tab ── */
  const [assignmentList, setAssignmentList] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignGtpSelected, setAssignGtpSelected] = useState(null);
  const [assignInspTestType, setAssignInspTestType] = useState("");
  const [assignMatchCount, setAssignMatchCount] = useState(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignGtpCharacteristics, setAssignGtpCharacteristics] = useState([]);
  const [assignStdValues, setAssignStdValues] = useState({});

  const [showEditAssignModal, setShowEditAssignModal] = useState(false);
  const [editAssignRow, setEditAssignRow] = useState(null);
  const [editAssignChars, setEditAssignChars] = useState([]);
  const [editAssignStdValues, setEditAssignStdValues] = useState({});
  const [editAssignSaving, setEditAssignSaving] = useState(false);
  const [editAssignMode, setEditAssignMode] = useState("view");
  const [allTestTypes, setAllTestTypes] = useState([]);

  /* ── Assignment tab filter (cascading, interlinked) ── */
  const [assignProductType, setAssignProductType] = useState("");
  const [prodFilterSelections, setProdFilterSelections] = useState({
    productCategory: [], subCategory: [], qualityCategory: [],
    cableType: [], code: [], colour: [],
    noOfCores: [], nominalCrossSection: [], referenceOfStandard: [],
    voltage: [], construction: [], productCode: [], testType: [],
  });
  const [assignProductCode, setAssignProductCode] = useState("");
  const [assignProductDesc, setAssignProductDesc] = useState("");
  const [prodMatchCount, setProdMatchCount] = useState(null);
  const [assignFilterOptions, setAssignFilterOptions] = useState({});

  /* Scoped filter options for the Assignment tab (replaces deriving
     these from the old allProducts full-table dump). Refetched whenever
     the product type or any upstream selection narrows the scope. */
  useEffect(() => {
    axios.get(`${API}/assignment-filter-options`, {
      params: {
        productType: assignProductType,
        productCategory: optsToParam(prodFilterSelections.productCategory),
        subCategory: optsToParam(prodFilterSelections.subCategory),
        qualityCategory: optsToParam(prodFilterSelections.qualityCategory),
        cableType: optsToParam(prodFilterSelections.cableType),
        code: optsToParam(prodFilterSelections.code),
        colour: optsToParam(prodFilterSelections.colour),
      },
    })
      .then(r => setAssignFilterOptions(r.data || {}))
      .catch(() => setAssignFilterOptions({}));
  }, [
    assignProductType,
    prodFilterSelections.productCategory, prodFilterSelections.subCategory,
    prodFilterSelections.qualityCategory, prodFilterSelections.cableType,
    prodFilterSelections.code, prodFilterSelections.colour,
  ]);

  /* Single-row product code <-> description lookups (replaces
     allProducts.find(...) client-side search). */
  const lookupProductByCode = async (code) => {
    if (!code) return null;
    try {
      const r = await axios.get(`${API}/product-lookup`, { params: { productCode: code } });
      return r.data || null;
    } catch { return null; }
  };
  /* Type-ahead search for the Assignment tab's Product Code / Description
     dropdowns (max 50 rows, scoped by the selected filters). */
  const searchProducts = async (text) => {
    try {
      const params = buildAssignParams(prodFilterSelections, assignProductType, "");
      params.q = text || undefined;
      const r = await axios.get(`${API}/product-search`, { params });
      return r.data || [];
    } catch { return []; }
  };
  const lookupProductByDescription = async (description) => {
    if (!description) return null;
    try {
      const r = await axios.get(`${API}/product-lookup`, { params: { productDescription: description } });
      return r.data || null;
    } catch { return null; }
  };

  /* ── Product Format cascading filter state ── */
  const [pfFilterSelections, setPfFilterSelections] = useState({
    product_type: [], product_category: [], sub_category: [], quality_category: [],
    cable_type: [], code: [], color: [], cores: [], cross_section: [],
    std_ref: [], voltage: [], construction: [],
  });
  const [pfTemplateNoFilter,   setPfTemplateNoFilter]   = useState("");
  const [pfInspTestTypeFilter, setPfInspTestTypeFilter] = useState("");
  const [pfProductCodeFilter,  setPfProductCodeFilter]  = useState("");
  const [pfProductDescFilter,  setPfProductDescFilter]  = useState("");
  const pfActiveFilterCount = Object.values(pfFilterSelections).reduce((s, a) => s + (a?.length || 0), 0)
    + (pfTemplateNoFilter ? 1 : 0) + (pfInspTestTypeFilter ? 1 : 0)
    + (pfProductCodeFilter ? 1 : 0) + (pfProductDescFilter ? 1 : 0);

  /* ── Product Format tab ── */
  const [productFormatList, setProductFormatList] = useState([]);
  const [showPfModal, setShowPfModal] = useState(false);
  const [pfEditRow, setPfEditRow] = useState(null);
  const [pfStdValues, setPfStdValues] = useState({});
  const [isStdValueEditMode, setIsStdValueEditMode] = useState(false);
  const [pfModalTab, setPfModalTab] = useState("parameters");
  const [showPfViewModal, setShowPfViewModal] = useState(false);
  const pfReportRef = useRef();
  const pfLoadingRef = useRef(false); // guards against overlapping loadProductFormats() calls
  const pfPendingPageRef = useRef(null); // most recent page requested while a load was already in flight
  const [pfLoading, setPfLoading] = useState(false);

  /* Server-side pagination for the Product Format tab. Filtering also now
     happens server-side (see loadProductFormats) — productFormatList only
     ever holds the current page (≤ PF_PAGE_SIZE rows), never the full
     24k+ row table. */
  const PF_PAGE_SIZE = 50;
  const [pfPage, setPfPage] = useState(1);
  const [pfTotal, setPfTotal] = useState(0);
  // Distinct dropdown option values for the filter bar, fetched once from
  // a small dedicated endpoint instead of being derived from the (now
  // page-only) productFormatList.
  const [pfFilterOptions, setPfFilterOptions] = useState({});

  /* ══════════════════════════════════════════════
     EFFECTS
  ══════════════════════════════════════════════ */
  useEffect(() => {
    loadDropdowns();
    loadMasterProductTypes();
    loadTemplates();
    loadAssignments();
    loadPfFilterOptions();
    loadProductFormats(1);
    axios.get(`${MASTER_API}/configuration/inspection-test-type`)
      .then(r => setAllInspTestTypes(r.data || []))
      .catch(console.error);
    axios.get(`${MASTER_API}/configuration/dynamic/test-type`)
      .then(r => setAllTestTypes(r.data || []))
      .catch(console.error);
  }, []);

  // When productType changes, filter inspection types and test types from the config table
  useEffect(() => {
    if (!formData.productType) {
      setQualityConfigurations([]);
      setFilteredInspTypes([]);
      setFilteredTestTypes([]);
      return;
    }
    const filtered = allInspTestTypes.filter(r =>
      (r.product_type || "").toLowerCase() === formData.productType.toLowerCase()
    );
    setFilteredTestTypes(filtered.map(r => r.name).filter(Boolean));
    setFilteredInspTypes(filtered.map(r => ({ value: r.name, label: r.name })));

    axios.get(`${MASTER_API}/quality-configuration/module-characteristics`, {
      params: { productType: formData.productType, module: "INSPECTION" },
    }).then(r => setQualityConfigurations(groupByProcess(r.data || [])))
      .catch(console.error);
  }, [formData.productType, allInspTestTypes]);

  /* Product Format tab: any filter change resets to page 1 and re-fetches
     from the server (debounced so typing in the text filters doesn't fire
     a request per keystroke). Page-only changes (Prev/Next) are handled
     by the separate effect below instead, since they shouldn't reset. */
  const pfFilterMountRef = useRef(true); // skip the very first run — mount already loads page 1
  useEffect(() => {
    if (pfFilterMountRef.current) { pfFilterMountRef.current = false; return; }
    const t = setTimeout(() => {
      setPfPage(1);
      loadProductFormats(1);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfFilterSelections, pfTemplateNoFilter, pfInspTestTypeFilter, pfProductCodeFilter, pfProductDescFilter]);

  /* Product Format tab: page navigation (Prev/Next/page-jump). Skips page 1
     since the filter effect above already fetches page 1 on every filter
     change (avoids a duplicate request when a filter change also resets
     pfPage to 1). */
  useEffect(() => {
    if (pfPage === 1) return;
    loadProductFormats(pfPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfPage]);

  /* live match count while filters change */
  useEffect(() => {
    const params = buildAssignParams(prodFilterSelections, assignProductType, assignProductCode);
    const hasAny = Object.values(params).some(Boolean);
    if (!hasAny) { setProdMatchCount(null); return; }
    axios.get(`${API}/count-matching`, { params })
      .then(r => setProdMatchCount(r.data?.count ?? null))
      .catch(() => setProdMatchCount(null));
  }, [prodFilterSelections, assignProductType, assignProductCode]);

useEffect(() => {
  if (!assignGtpSelected) { setAssignGtpCharacteristics([]); return; }
  axios.get(`${API}/templates/${encodeInspNo(assignGtpSelected.value)}`)
    .then(r => {
      const chars = r.data.characteristics || [];
      // deduplicate — show each characteristic only once
      const seen = new Set();
      const unique = chars.filter(c => {
        const key = (c.characteristic || "").trim().toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).map(c => ({ ...c, type: c.valueType || c.type || "" }));
      setAssignGtpCharacteristics(unique);
      setAssignStdValues({});
    })
    .catch(console.error);
}, [assignGtpSelected]);

  /* ══════════════════════════════════════════════
     HELPERS
  ══════════════════════════════════════════════ */
  const encodeInspNo = (no) => String(no).replace(/\//g, "__");

  const groupByProcess = (rows) => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.process_name || "";
      if (!map.has(key)) map.set(key, { process_id: r.process_id, process_name: key, characteristics: [] });
      const g = map.get(key);
      if (!g.characteristics.some(c => c.characteristic === r.characteristic)) {
        g.characteristics.push({
          process_id: r.process_id, process_name: r.process_name,
          sub_process_id: r.sub_process_id, sub_process_name: r.sub_process_name || "",
          characteristic: r.characteristic, uom: r.uom || "",
          test_type: r.test_type || "", value_type: r.value_type || "",
          sequence: r.sequence ?? null,
        });
      }
    });
    return [...map.values()];
  };
// Add this helper near the top of the component (after allInspTestTypes is declared):
const getSequenceForTestType = useCallback((testTypeName) => {
  if (!testTypeName || !formData.productType) return null;
  const found = allInspTestTypes.find(
    r => r.name?.toLowerCase() === testTypeName.toLowerCase()
      && r.product_type?.toLowerCase() === formData.productType.toLowerCase()
  );
  return found?.sequence ?? null;
}, [allInspTestTypes, formData.productType]);
  const buildAssignParams = (sel, productType, productCode) => ({
    productType:          productType                                              || undefined,
    productCategory:      sel.productCategory?.map(o => o.value).join(",")        || undefined,
    subCategory:          sel.subCategory?.map(o => o.value).join(",")            || undefined,
    qualityCategory:      sel.qualityCategory?.map(o => o.value).join(",")        || undefined,
    cableType:            sel.cableType?.map(o => o.value).join(",")              || undefined,
    code:                 sel.code?.map(o => o.value).join(",")                   || undefined,
    colour:               sel.colour?.map(o => o.value).join(",")                 || undefined,
    noOfCores:            sel.noOfCores?.map(o => o.value).join(",")              || undefined,
    nominalCrossSection:  sel.nominalCrossSection?.map(o => o.value).join(",")    || undefined,
    referenceOfStandard:  sel.referenceOfStandard?.map(o => o.value).join(",")    || undefined,
    voltage:              sel.voltage?.map(o => o.value).join(",")                || undefined,
    construction:         sel.construction?.map(o => o.value).join(",")           || undefined,
    productCode:          productCode                                              || undefined,
    testType:             sel.testType?.map(o => o.value).join(",")               || undefined,
  });

  /* ══════════════════════════════════════════════
     API LOADERS
  ══════════════════════════════════════════════ */
  const loadDropdowns = async () => {
    try {
      const r = await axios.get(`${API}/dropdowns`);
      const base = r.data || {};
      setAllProducts(base.allProducts || []);
      // productTypes is intentionally NOT taken from `base` here — it's
      // sourced from the master API via loadMasterProductTypes() instead
      // (this endpoint's productTypes comes from productdetails, a
      // different DB, and shouldn't be mixed with the master list). Keep
      // whatever loadMasterProductTypes has already set, regardless of
      // which of the two calls resolves first.
      setDdl(prev => ({
        ...base,
        productTypes:         prev.productTypes,
        codes:                base.codes || [],
        colours:              base.colours || [],
        cores:                base.cores || [],
        nominalCrossSections: base.nominalCrossSections || [],
        standards:            base.standards || [],
        voltages:             base.voltages || [],
        productCodes:         base.productCodes || [],
        testTypes:            base.testTypes || [],
        categoryToCableTypes: base.categoryToCableTypes || {},
        cableTypeToCode:      base.cableTypeToCode || {},
        categoryToSubCategory: base.categoryToSubCategory || {},
        subCategoryToQualityCategory: base.subCategoryToQualityCategory || {},
      }));
    } catch (e) { console.error(e); }
  };

  // Product Type dropdown is sourced independently, straight from the
  // master API's configuration list — not from ${API}/dropdowns (which
  // reads product types out of productdetails, a different database
  // than the master product-types config, so it can't be reused here).
  const loadMasterProductTypes = async () => {
    try {
      const r = await axios.get(`${MASTER_API}/configuration/product-types`);
      const opts = (r.data || []).map(pt => {
        const name = typeof pt === "string" ? pt : (pt.name || pt.product_type || "");
        return { value: name, label: name };
      }).filter(o => o.value);
      setDdl(prev => ({ ...prev, productTypes: opts }));
    } catch (e) { console.error("Failed to load master product types", e); }
  };

  const loadTemplates = async () => {
    try {
      const r = await axios.get(`${API}/templates`);
      const payload = r.data;
      // Backend paginates this endpoint (returns { total, page, limit, data }).
      // Guard for a plain-array response too, in case that ever changes back.
      const list = Array.isArray(payload) ? payload : (payload?.data || []);
      setTemplateList(list);
    }
    catch (e) { console.error(e); }
  };

  const loadAssignments = async () => {
    try {
      const r = await axios.get(`${API}/assignments`);
      const payload = r.data;
      // Backend paginates this endpoint too (returns { total, page, limit, data }).
      const list = Array.isArray(payload) ? payload : (payload?.data || []);
      setAssignmentList(list);
    }
    catch (e) { console.error(e); }
  };

  // Build the query params for one page request from current filter state.
  const buildPfParams = (page) => {
    const sel = pfFilterSelections;
    return {
      page, limit: PF_PAGE_SIZE,
      productType:        sel.product_type?.map(o => o.value).join(",")     || undefined,
      productCategory:    sel.product_category?.map(o => o.value).join(",") || undefined,
      subCategory:        sel.sub_category?.map(o => o.value).join(",")     || undefined,
      qualityCategory:    sel.quality_category?.map(o => o.value).join(",") || undefined,
      cableType:          sel.cable_type?.map(o => o.value).join(",")       || undefined,
      code:               sel.code?.map(o => o.value).join(",")             || undefined,
      colour:             sel.color?.map(o => o.value).join(",")            || undefined,
      cores:              sel.cores?.map(o => o.value).join(",")            || undefined,
      crossSection:       sel.cross_section?.map(o => o.value).join(",")    || undefined,
      stdRef:             sel.std_ref?.map(o => o.value).join(",")          || undefined,
      voltage:            sel.voltage?.map(o => o.value).join(",")          || undefined,
      construction:       sel.construction?.map(o => o.value).join(",")     || undefined,
      productCode:        pfProductCodeFilter  || undefined,
      productDescription: pfProductDescFilter  || undefined,
      inspectionNo:       pfTemplateNoFilter   || undefined,
      inspectionTestType: pfInspTestTypeFilter || undefined,
    };
  };

  // Fetches exactly ONE page (≤ PF_PAGE_SIZE rows) matching the current
  // filters — replaces the old "fetch all ~120 pages concurrently, then
  // filter 24k rows in the browser" approach, which is what was
  // overloading Node.js and freezing the tab on large tables.
  const loadProductFormats = async (page = pfPage) => {
    // Guard against overlapping requests (e.g. rapid filter changes). A
    // request that arrives while one is already in flight used to be
    // dropped outright — e.g. type a product code, the in-flight request
    // from the previous keystroke swallows it, and the list just never
    // updates to match what's actually typed. Instead, remember the
    // latest requested page and re-run once the in-flight call finishes,
    // so the UI always converges on the most recent filters.
    if (pfLoadingRef.current) { pfPendingPageRef.current = page; return; }
    pfLoadingRef.current = true;
    setPfLoading(true);
    try {
      const r = await axios.get(`${API}/product-formats`, { params: buildPfParams(page) });
      const payload = r.data;

      if (Array.isArray(payload)) {
        // Backward-compat: an un-paginated backend just returns everything.
        setProductFormatList(payload);
        setPfTotal(payload.length);
        return;
      }

      const { data = [], total } = payload || {};
      setProductFormatList(data);
      if (page === 1 && typeof total === "number") setPfTotal(total);
    } catch (e) {
      console.error(e);
    } finally {
      pfLoadingRef.current = false;
      setPfLoading(false);
      if (pfPendingPageRef.current !== null) {
        const nextPage = pfPendingPageRef.current;
        pfPendingPageRef.current = null;
        loadProductFormats(nextPage);
      }
    }
  };

  const loadPfFilterOptions = async () => {
    try {
      const r = await axios.get(`${API}/product-formats/filter-options`);
      setPfFilterOptions(r.data || {});
    } catch (e) { console.error(e); }
  };

  /* ══════════════════════════════════════════════
     TEMPLATE — CRUD
  ══════════════════════════════════════════════ */
  const resetForm = () => {
    setFormData({ inspectionNo: "", productType: "", inspectionType: [] });
    setSelectedCharacteristics({});
    setCharMeta({});
    setQualityConfigurations([]);
    setFilteredInspTypes([]);
    setFilteredTestTypes([]);
    setIsViewMode(false);
    setIsEditMode(false);
    setEditInspNo(null);
    setActiveViewTab("particulars");
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleView = async (inspNo) => {
    try {
      const r = await axios.get(`${API}/templates/${encodeInspNo(inspNo)}`);
      const d = r.data;
      setFormData({
        inspectionNo: d.inspection_no,
        productType: d.product_type,
        inspectionType: d.inspection_type
          ? String(d.inspection_type).split(",").map(s => s.trim()).filter(Boolean)
          : [],
      });
      setEditInspNo(d.inspection_no);
      const sc = {};
      const cm = {};
      (d.characteristics || []).forEach(c => {
        sc[`CHAR__${c.processName}__${c.characteristic}`] = {
          ...c,
          type: c.valueType || c.type || "",   // service returns valueType; normalize here
          variable: c.isFormula ? (c.formula || "") : (c.variable || ""),
          isFormula: c.isFormula ? "Yes" : "No",
        };
        sc[`GROUP__${c.processName}`] = true;
        cm[`CHAR__${c.processName}__${c.characteristic}`] = {
          testType: c.testType || "",
          type: c.valueType || c.type || "",   // service returns valueType
          variable: c.isFormula ? (c.formula || "") : (c.variable || ""),
          isFormula: c.isFormula ? "Yes" : "No",
          sequence: c.sequence ?? null,
        };
      });
      setSelectedCharacteristics(sc);
      setCharMeta(cm);
      setIsViewMode(true);
      setIsEditMode(false);
      setShowModal(true);
    } catch (e) { toast.error("Failed to load template"); }
  };

  const handleEdit = async (inspNo) => {
    await handleView(inspNo);
    setIsViewMode(false);
    setIsEditMode(true);
  };

  // Returns the first duplicated variable name (case-insensitive), or null.
  const findDuplicateVariable = (charEntries) => {
    const seen = new Set();
    for (const [k] of charEntries) {
      const v = (charMeta[k]?.variable || "").trim();
      if (!v) continue;
      const nk = v.toLowerCase();
      if (seen.has(nk)) return v;
      seen.add(nk);
    }
    return null;
  };

  // Validates variables (normal rows) and formulas (Formula = Yes rows).
  // Returns an error message, or null when everything is fine.
  const validateVarsAndFormulas = (charEntries) => {
    const VAR_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
    const vars = new Set();
    for (const [k] of charEntries) {
      const meta = charMeta[k] || {};
      if (meta.isFormula === "Yes") continue;
      const name = (meta.variable || "").trim();
      if (!name) continue;
      if (!VAR_RE.test(name)) return `Variable "${name}" is invalid. Use letters, numbers and underscore only, starting with a letter.`;
      if (vars.has(name.toLowerCase())) return `Variable "${name}" is used more than once. Each variable must be unique.`;
      vars.add(name.toLowerCase());
    }
    for (const [k, v] of charEntries) {
      const meta = charMeta[k] || {};
      if (meta.isFormula !== "Yes") continue;
      const label = v.characteristic;
      const f = (meta.variable || "").trim();
      if (!f) return `Enter a formula for "${label}" or set Formula to No.`;
      if (!/^[A-Za-z0-9_+\-*/().\s]+$/.test(f)) return `Formula for "${label}" has invalid characters. Use variables, numbers and + - * / ( ) only.`;
      let depth = 0;
      for (const ch of f) { if (ch === "(") depth++; if (ch === ")") depth--; if (depth < 0) break; }
      if (depth !== 0) return `Formula for "${label}" has unbalanced brackets.`;
      const ids = f.match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
      if (ids.length === 0) return `Formula for "${label}" must use at least one variable.`;
      const bad = ids.find(id => !vars.has(id.toLowerCase()));
      if (bad) return `Formula for "${label}" uses "${bad}", which is not a variable in this template.`;
    }
    return null;
  };

  const handleSave = async () => {
    const charEntries = Object.entries(selectedCharacteristics).filter(([k]) => !k.startsWith("GROUP__"));
    if (!formData.inspectionNo.trim()) { alert("Inspection Number is mandatory"); return; }
    if (!formData.productType)         { alert("Product Type is mandatory"); return; }

    // Inspection Test Type is mandatory if the selected product type has types configured
    if (filteredInspTypes.length > 0 && (!formData.inspectionType || formData.inspectionType.length === 0)) {
      alert("Inspection Test Type is mandatory");
      return;
    }

    if (charEntries.length === 0) { alert("Select at least one characteristic"); return; }

    const varErr = validateVarsAndFormulas(charEntries);
    if (varErr) { alert(varErr); return; }

    // ── FIX: include sequence in payload ──
    const characteristics = charEntries.map(([k, v]) => ({
      processName:    v.processName    || v.process_name    || "",
      subProcessName: v.subProcessName || v.sub_process_name || "",
      characteristic: v.characteristic || "",
      uom:            v.uom || "",
      testType:       charMeta[k]?.testType || "",
      type:           charMeta[k]?.type     || "",
      // Formula = Yes  -> the cell text is the FORMULA (no variable name)
      // Formula = No   -> the cell text is the VARIABLE name
      variable:       charMeta[k]?.isFormula === "Yes" ? "" : (charMeta[k]?.variable || "").trim(),
      formula:        charMeta[k]?.isFormula === "Yes" ? (charMeta[k]?.variable || "").trim() : "",
      isFormula:      charMeta[k]?.isFormula === "Yes",
      sequence: getSequenceForTestType(charMeta[k]?.testType) ?? charMeta[k]?.sequence ?? v.sequence ?? null,

    }));

    try {
      await axios.post(`${API}/templates/save`, {
        inspectionNo:    formData.inspectionNo.trim(),
        productType:     formData.productType,
        inspectionTypes: formData.inspectionType,
        characteristics,
      });
      toast.success("Saved Successfully.");
      setShowModal(false);
      resetForm();
      loadTemplates();
    } catch (e) { alert(e.response?.data?.message || "Save Failed"); }
  };

  const handleUpdate = async () => {
    const charEntries = Object.entries(selectedCharacteristics).filter(([k]) => !k.startsWith("GROUP__"));
    if (charEntries.length === 0) { alert("Select at least one characteristic"); return; }

    const varErr = validateVarsAndFormulas(charEntries);
    if (varErr) { alert(varErr); return; }

    // ── FIX: include sequence in payload ──
    const characteristics = charEntries.map(([k, v]) => ({
      processName:    v.processName    || v.process_name    || "",
      subProcessName: v.subProcessName || v.sub_process_name || "",
      characteristic: v.characteristic || "",
      uom:            v.uom || "",
      testType:       charMeta[k]?.testType || "",
      type:           charMeta[k]?.type     || "",
      // Formula = Yes  -> the cell text is the FORMULA (no variable name)
      // Formula = No   -> the cell text is the VARIABLE name
      variable:       charMeta[k]?.isFormula === "Yes" ? "" : (charMeta[k]?.variable || "").trim(),
      formula:        charMeta[k]?.isFormula === "Yes" ? (charMeta[k]?.variable || "").trim() : "",
      isFormula:      charMeta[k]?.isFormula === "Yes",
   sequence: getSequenceForTestType(charMeta[k]?.testType) ?? charMeta[k]?.sequence ?? v.sequence ?? null,

    }));

    const body = {
      productType:     formData.productType,
      inspectionTypes: formData.inspectionType,
      characteristics,
    };
    // DEBUG: open browser DevTools > Console to see exactly what is sent
    console.log("UPDATE URL:", `${API}/templates/${encodeInspNo(editInspNo)}`);
    console.log("UPDATE PAYLOAD:", JSON.stringify(body.characteristics.map(c => ({
      ch: c.characteristic, variable: c.variable, isFormula: c.isFormula,
    })), null, 2));

    try {
      const res = await axios.put(`${API}/templates/${encodeInspNo(editInspNo)}`, body);
      console.log("UPDATE RESPONSE:", res.data);

      // The patched backend returns rowsWritten. If it is missing, the
      // server that answered is still running OLD code (or a different server).
      if (res.data?.rowsWritten === undefined) {
        alert("Server replied OK but did NOT confirm rows written.\n" +
              "The backend is running old code or a different server. " +
              "Restart Node and check the API URL: " + API);
        return;
      }

      toast.success(`Updated Successfully (${res.data.rowsWritten} rows saved)`);
      setShowModal(false);
      resetForm();
      loadTemplates();
    } catch (e) { alert(e.response?.data?.message || "Update Failed"); }
  };

  const handleDeleteTemplate = async (inspNo) => {
    if (!window.confirm(`Delete template "${inspNo}" and all its assignments?`)) return;
    try {
      await axios.delete(`${API}/templates/${encodeInspNo(inspNo)}`);
      toast.success("Template deleted");
      loadTemplates(); loadAssignments();
      setPfPage(1);
      loadProductFormats(1);
    } catch (e) { toast.error(e.response?.data?.message || "Delete failed"); }
  };

  /* ── characteristic selection ── */
  const handleCharacteristicSelect = (group, item) => {
    const key = `CHAR__${item.process_name}__${item.characteristic?.trim()}`;
    setSelectedCharacteristics(prev => {
      const u = { ...prev };
      if (u[key]) delete u[key];
      else u[key] = {
        processName:    item.process_name,
        subProcessName: item.sub_process_name || "",
        characteristic: item.characteristic,
        uom:            item.uom || "",
        testType:       item.test_type || "",
        valueType:      item.value_type || "",
        sequence:       item.sequence ?? null,   // ── FIX ──
      };
      return u;
    });
  };

  const handleGroupSelect = (groupName, characteristics) => {
    const groupKey = `GROUP__${groupName}`;
    setSelectedCharacteristics(prev => {
      const u = { ...prev };
      if (u[groupKey]) {
        delete u[groupKey];
        characteristics.forEach(item => delete u[`CHAR__${item.process_name}__${item.characteristic?.trim()}`]);
      } else {
        u[groupKey] = true;
      }
      return u;
    });
  };

  const viewCharacteristics = Object.entries(selectedCharacteristics)
    .filter(([k]) => !k.startsWith("GROUP__"))
    .sort(([, a], [, b]) => {
      const pA = (a.processName || "").toUpperCase(), pB = (b.processName || "").toUpperCase();
      if (pA < pB) return -1; if (pA > pB) return 1; return 0;
    });

  /* ══════════════════════════════════════════════
     ASSIGNMENT — handlers
  ══════════════════════════════════════════════ */
  const prodActiveFilterCount = Object.values(prodFilterSelections).reduce((s, a) => s + (a?.length || 0), 0)
    + (assignProductType ? 1 : 0) + (assignProductCode ? 1 : 0);

  const handleOpenAssignModal = () => {
    if (!assignProductType) { alert("Please select a Product Type to assign an Inspection"); return; }
    setAssignGtpSelected(null);
    setAssignGtpCharacteristics([]);
    setAssignStdValues({});
    setAssignMatchCount(prodMatchCount);
    setShowAssignModal(true);
  };

const handleAssignSave = async () => {
  if (!assignGtpSelected) { alert("Select an Inspection Template"); return; }
  setAssignSaving(true);
 const stdValues = assignGtpCharacteristics.map(c => ({
  characteristic: c.characteristic,
  uom:            c.uom || "",
  testType:       c.testType  || "",   // ← ADD
  valueType:      c.valueType || "",   // ← simplified, now works after Fix 1
  standardValue:  assignStdValues[c.characteristic] || "",
  minValue: "", maxValue: "",
}));
  try {
    const params = buildAssignParams(prodFilterSelections, assignProductType, assignProductCode);
    const r = await axios.post(`${API}/assignments/save`, {
      inspectionNo:        assignGtpSelected.value,
      productType:         assignProductType          || null,
      productCategory:     params.productCategory     || null,
      subCategory:         params.subCategory         || null,
      qualityCategory:     params.qualityCategory     || null,
      cableType:           params.cableType           || null,
      construction:        params.construction        || null,
      code:                params.code                || null,
      colour:              params.colour              || null,
      noOfCores:           params.noOfCores           || null,
      nominalCrossSection: params.nominalCrossSection || null,
      referenceOfStandard: params.referenceOfStandard || null,
      voltage:             params.voltage             || null,
      productCode:         params.productCode         || null,
      stdValues,
      // ← inspectionTestType REMOVED — backend reads from template
    });
    toast.success(`Assigned — ${r.data.productsLinked} product(s), ${r.data.assignmentIds?.length || 1} test type(s)`);
    setShowAssignModal(false);
    setAssignGtpSelected(null);
    setAssignGtpCharacteristics([]);
    setAssignStdValues({});
    setAssignInspTestType("");
    loadAssignments();
    setPfPage(1);
    loadProductFormats(1);
  } catch (e) { alert(e.response?.data?.message || "Save failed"); }
  finally { setAssignSaving(false); }
};

const handleOpenEditAssign = async (row, mode = "view") => {
  setEditAssignRow(row);
  setEditAssignMode(mode);
  setEditAssignChars([]);
  setEditAssignStdValues({});
  try {
    const r = await axios.get(`${API}/templates/${encodeInspNo(row.inspection_no)}`);
    const chars = r.data.characteristics || [];

    // deduplicate by characteristic name
    const seen = new Set();
    const unique = chars.filter(c => {
      const key = (c.characteristic || "").trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(c => ({ ...c, type: c.valueType || c.type || "" }));

    setEditAssignChars(unique);
    const sv = {};
    (row.std_values || []).forEach(c => { sv[c.characteristic] = c.standardValue || ""; });
    setEditAssignStdValues(sv);
  } catch (e) { console.error(e); }
  setShowEditAssignModal(true);
};

  const handleEditAssignSave = async () => {
    setEditAssignSaving(true);
   const stdValues = editAssignChars.map(c => ({
  characteristic: c.characteristic,
  testType:       c.testType  || c.test_type  || "",   // ← ADD
  valueType:      c.valueType || c.value_type || "",
  standardValue:  editAssignStdValues[c.characteristic] ?? "",
}));
    try {
      await axios.put(`${API}/assignments/${editAssignRow.assignment_id}`, { stdValues });
      toast.success("Assignment updated — all product formats propagated");
      setShowEditAssignModal(false);
      setEditAssignRow(null);
      setEditAssignChars([]);
      setEditAssignStdValues({});
      setEditAssignMode("view");
      loadAssignments();
      loadProductFormats();
    } catch (e) { alert(e.response?.data?.message || "Save failed"); }
    finally { setEditAssignSaving(false); }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this assignment and unlink all products?")) return;
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`);
      toast.success("Assignment removed");
      loadAssignments();
      setPfPage(1);
      loadProductFormats(1);
    } catch (e) { toast.error("Delete failed"); }
  };

  /* ══════════════════════════════════════════════
     PRODUCT FORMAT — handlers
  ══════════════════════════════════════════════ */
// Normalize characteristic text the same way everywhere it's used as a
// lookup key — trim + lowercase — so trivial whitespace/casing differences
// between inspection_product_format.parameters and inspection_templates.characteristic
// don't cause a saved standard_value to silently disappear.
const normChar = (s) => (s || "").trim().toLowerCase();

const handleViewProductFormat = async (row) => {
  const sv = {};
  (row.characteristics || []).forEach(c => {
    // Only record a key when there's a real saved standard_value — an empty
    // string here would later overwrite (and hide) a valid value pulled
    // from inspection_value_master when the two are merged below.
    if (c.standard_value !== undefined && c.standard_value !== null && c.standard_value !== "") {
      sv[normChar(c.characteristic)] = c.standard_value;
    }
  });

  // Auto-fetch values already entered in inspection_value_master for this
  // product code, keyed by parameter label (normalized the same way), so
  // they can pre-fill any Standard Value box that hasn't been explicitly
  // saved on the product format yet.
  const masterValues = {};
  try {
    const mv = await axios.get(`${API}/values/${encodeURIComponent(row.product_code)}`);
    (mv.data?.fields || []).forEach(f => {
      if (f.value !== "" && f.value != null) masterValues[normChar(f.parameterName)] = f.value;
    });
  } catch (e) {
    console.error("Failed to load inspection_value_master values", e);
  }

  // inspection_product_format's own saved standard_value wins if present;
  // otherwise fall back to whatever was entered in inspection_value_master.
  const combined = { ...masterValues, ...sv };
  setPfStdValues(combined);

  try {
    const r = await axios.get(`${API}/templates/${encodeInspNo(row.inspection_no)}`);
    const templateChars = r.data.characteristics || [];

    // deduplicate by characteristic name
    const seen = new Set();
    const uniqueChars = templateChars.filter(tc => {
      const key = normChar(tc.characteristic);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const merged = uniqueChars.map(tc => ({
      characteristic: tc.characteristic,
      process_name:   tc.processName   || tc.process_name   || "",
      test_type:      tc.testType      || tc.test_type      || "",
      uom:            tc.uom           || "",
      standard_value: combined[normChar(tc.characteristic)] || "",
      value_type:     tc.type          || tc.value_type     || "",
      variable:       tc.variable      || "",
      formula:        tc.formula       || "",
      isFormula:      !!tc.isFormula,
    }));

    setPfEditRow({ ...row, characteristics: merged });
  } catch (e) {
    console.error("Failed to load template for report", e);
    setPfEditRow(row);
  }

  setIsStdValueEditMode(false);
  setPfModalTab("parameters");
  setShowPfModal(true);
};

  const handleProductStdValueSave = async () => {
    const characteristics = (pfEditRow.characteristics || []).map(c => ({
      characteristic: c.characteristic,
      uom:            c.uom || "",
      value_type:     c.value_type || "",
      standard_value: pfStdValues[normChar(c.characteristic)] || "",
    }));
    try {
      await axios.put(`${API}/product-formats/${pfEditRow.id}`, { characteristics });
      toast.success("Product format updated");
      setShowPfModal(false);
      setIsStdValueEditMode(false);
      loadProductFormats();
    } catch (e) { toast.error(e.response?.data?.message || "Update failed"); }
  };

  const handleDeleteProductFormat = async (id, productCode) => {
    if (!window.confirm(`Unlink product "${productCode}" from inspection?`)) return;
    try {
      await axios.delete(`${API}/product-formats/${id}`);
      toast.success("Product unlinked");
      // Row count changed — go back to page 1 so pfTotal (only refreshed
      // on page 1) stays accurate rather than going stale.
      setPfPage(1);
      loadProductFormats(1);
    } catch (e) { toast.error("Delete failed"); }
  };

  /* ══════════════════════════════════════════════
     COMPUTED
  ══════════════════════════════════════════════ */
 const templateOptions = templateList.map(t => ({
  value: t.inspection_no,
  label: t.inspection_no,
}));

  /* ══════════════════════════════════════════════
     STYLES
  ══════════════════════════════════════════════ */
  const customSelectStyles = {
    control: (base) => ({ ...base, minHeight: 32, fontSize: "13px", borderColor: "#dee2e6", boxShadow: "none", "&:hover": { borderColor: "#adb5bd" } }),
    valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base }),
    option: (base, state) => ({ ...base, fontSize: "13px", backgroundColor: state.isSelected ? "#021843" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
    multiValue: (base) => ({ ...base, fontSize: "12px", backgroundColor: "#e2e8f0", borderRadius: 4 }),
    multiValueLabel: (base) => ({ ...base, fontSize: "12px", color: "#334155", padding: "1px 4px" }),
    multiValueRemove: (base) => ({ ...base, color: "#64748b", ":hover": { backgroundColor: "#cbd5e1", color: "#1e293b" } }),
    menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  };

  const filterSelectStyles = {
    control: (base) => ({ ...base, minHeight: 30, height: "auto", fontSize: "12px", borderColor: "#dee2e6", boxShadow: "none", "&:hover": { borderColor: "#adb5bd" } }),
    valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
    indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", paddingTop: 2 }),
    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
    clearIndicator: (base) => ({ ...base, padding: 2 }),
    placeholder: (base) => ({ ...base, fontSize: "12px", color: "#94a3b8", fontWeight: 400 }),
    option: (base, state) => ({ ...base, fontSize: "12px", fontWeight: 400, color: state.isSelected ? "#fff" : "#334155", backgroundColor: state.isSelected ? "#475569" : state.isFocused ? "#f1f5f9" : "#fff", padding: "6px 10px" }),
    multiValue: (base) => ({ ...base, fontSize: "11px", backgroundColor: "#e2e8f0", borderRadius: 4 }),
    multiValueLabel: (base) => ({ ...base, fontSize: "11px", color: "#334155", padding: "1px 4px" }),
    multiValueRemove: (base) => ({ ...base, color: "#64748b", ":hover": { backgroundColor: "#cbd5e1", color: "#1e293b" } }),
    menu: (base) => ({ ...base, fontSize: "12px", zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  };

  /* Cascading cable types / codes for the generic product-format filter bar */
  const cascadedCableTypes = useMemo(() => {
    const selCats = prodFilterSelections.productCategory.map(o => o.value);
    if (selCats.length === 0) return ddl.cableTypes;
    const allowed = new Set();
    selCats.forEach(cat => {
      (ddl.categoryToCableTypes[cat] || []).forEach(ct => allowed.add(ct));
    });
    return ddl.cableTypes.filter(v => allowed.has(v));
  }, [prodFilterSelections.productCategory, ddl.cableTypes, ddl.categoryToCableTypes]);

  const cascadedCodes = useMemo(() => {
    const selCts = prodFilterSelections.cableType.map(o => o.value);
    if (selCts.length === 0) return ddl.codes;
    const allowed = new Set();
    selCts.forEach(ct => {
      (ddl.cableTypeToCode[ct] || []).forEach(c => allowed.add(c));
    });
    return ddl.codes.filter(v => allowed.has(v));
  }, [prodFilterSelections.cableType, ddl.codes, ddl.cableTypeToCode]);

  const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
      fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );

  /* Filtering now happens server-side (see loadProductFormats/buildPfParams)
     — productFormatList already IS the filtered, current page of rows, so
     it's rendered directly below instead of being re-filtered here. */

  /* ── Generic filter bar (Product Format tab) ── */
  const renderFilterBar = ({ filterSelections, setFilterSelections, matchCount, activeFilterCount, clearFilters, addLabel, onAdd }) => (
    <div style={styles.filterCard}>
      <div style={styles.filterBar}>
        {[
          { key: "productCategory",     opts: toOpts(ddl.productCategories), label: "Category" },
          { key: "cableType",           opts: toOpts(cascadedCableTypes),    label: "Cable Type" },
          { key: "code",                opts: toOpts(cascadedCodes),         label: "Code" },
          { key: "colour",              opts: toOpts(ddl.colours),           label: "Color" },
          { key: "noOfCores",           opts: toOpts(ddl.cores),             label: "Cores" },
          { key: "nominalCrossSection", opts: toOpts(ddl.nominalCrossSections), label: "Cross Section" },
          { key: "referenceOfStandard", opts: toOpts(ddl.standards),        label: "Std. Ref" },
          { key: "voltage",             opts: toOpts(ddl.voltages),          label: "Voltage" },
          { key: "construction",        opts: toOpts(ddl.constructions),     label: "Construction" },
        ].map(({ key, opts, label }) => (
          <div key={key} style={{ minWidth: 100, flex: "1 1 90px", maxWidth: 180, display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={styles.filterLabel}>{label}</span>
            <Select
              isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
              options={opts}
              value={filterSelections[key]}
              onChange={(selectedOpts) => {
                setFilterSelections(prev => {
                  const next = { ...prev, [key]: selectedOpts || [] };
                  if (key === "productCategory") { next.cableType = []; next.code = []; }
                  if (key === "cableType")       { next.code = []; }
                  return next;
                });
              }}
              placeholder={label} isClearable
              menuPortalTarget={document.body} menuPosition="fixed"
              styles={filterSelectStyles}
            />
          </div>
        ))}
        {activeFilterCount > 0 && (
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 3, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: "transparent" }}>x</span>
            <button onClick={clearFilters} style={styles.clearBtn}>✕ Clear ({activeFilterCount})</button>
          </div>
        )}
      </div>
      {matchCount !== null && activeFilterCount > 0 && (
        <div style={styles.matchHint}>
          <span style={{ color: matchCount > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
            {matchCount > 0 ? `${matchCount} product${matchCount !== 1 ? "s" : ""} match` : "No products match the current filters"}
          </span>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
        <button onClick={onAdd} style={{ background: "#021843", color: "#fff", border: "none", borderRadius: "4px", padding: "5px 16px", fontSize: "13px", fontWeight: 500, height: "32px", cursor: "pointer" }}>
          {addLabel}
        </button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
    <Layout>
      <div style={styles.pageTitleBar}>
        <div>
          <div style={styles.pageTitle}>Inspection Parameters</div>
          <div style={styles.pageSubtitle}>Manage Inspection Parameters Configuration</div>
        </div>
      </div>

      {/* ═══════════ TABLE TOGGLE ═══════════ */}
      <div style={styles.tableContainer}>
        <div style={{ ...styles.toggleBar, justifyContent: "flex-end" }}>
          <button style={{ ...styles.toggleBtn, ...(tableView === "template"   ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => setTableView("template")}>INSPECTION TEMPLATE</button>
          <button style={{ ...styles.toggleBtn, ...(tableView === "product"    ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => setTableView("product")}>ASSIGNMENT</button>
          <button style={{ ...styles.toggleBtn, ...(tableView === "assignment" ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => { setTableView("assignment"); loadProductFormats(); }}>PRODUCT FORMAT</button>
        </div>

        {/* ═══════════ TEMPLATE TAB ═══════════ */}
        {tableView === "template" && (
          <InspectionTemplateTab
            styles={styles} customSelectStyles={customSelectStyles} DeleteIcon={DeleteIcon} ddl={ddl}
            templateList={templateList} handleView={handleView} handleEdit={handleEdit}
            handleDeleteTemplate={handleDeleteTemplate} handleOpenCreate={handleOpenCreate}
            showModal={showModal} setShowModal={setShowModal} resetForm={resetForm}
            isViewMode={isViewMode} isEditMode={isEditMode} formData={formData} setFormData={setFormData}
            filteredInspTypes={filteredInspTypes} qualityConfigurations={qualityConfigurations}
            selectedCharacteristics={selectedCharacteristics} setSelectedCharacteristics={setSelectedCharacteristics}
            charMeta={charMeta} setCharMeta={setCharMeta}
            activeViewTab={activeViewTab} setActiveViewTab={setActiveViewTab} viewCharacteristics={viewCharacteristics}
            handleCharacteristicSelect={handleCharacteristicSelect} handleGroupSelect={handleGroupSelect}
            allTestTypes={allTestTypes} handleSave={handleSave} handleUpdate={handleUpdate}
          />
        )}

        {/* ═══════════ ASSIGNMENT TAB ═══════════ */}
        {tableView === "product" && (
          <InspectionAssignmentTab
            styles={styles} filterSelectStyles={filterSelectStyles} customSelectStyles={customSelectStyles}
            DeleteIcon={DeleteIcon} ddl={ddl}
            assignmentList={assignmentList} handleOpenEditAssign={handleOpenEditAssign} handleDeleteAssignment={handleDeleteAssignment}
            assignProductType={assignProductType} setAssignProductType={setAssignProductType}
            prodFilterSelections={prodFilterSelections} setProdFilterSelections={setProdFilterSelections}
            assignProductCode={assignProductCode} setAssignProductCode={setAssignProductCode}
            assignProductDesc={assignProductDesc} setAssignProductDesc={setAssignProductDesc}
            prodMatchCount={prodMatchCount}
            assignFilterOptions={assignFilterOptions}
            lookupProductByCode={lookupProductByCode} lookupProductByDescription={lookupProductByDescription}
            searchProducts={searchProducts}
            handleOpenAssignModal={handleOpenAssignModal}
            showAssignModal={showAssignModal} setShowAssignModal={setShowAssignModal}
            assignGtpSelected={assignGtpSelected} setAssignGtpSelected={setAssignGtpSelected}
            templateOptions={templateOptions}
            assignGtpCharacteristics={assignGtpCharacteristics} setAssignGtpCharacteristics={setAssignGtpCharacteristics}
            assignStdValues={assignStdValues} setAssignStdValues={setAssignStdValues} setAssignInspTestType={setAssignInspTestType}
            assignMatchCount={assignMatchCount} assignSaving={assignSaving} handleAssignSave={handleAssignSave}
            showEditAssignModal={showEditAssignModal} setShowEditAssignModal={setShowEditAssignModal}
            editAssignRow={editAssignRow} setEditAssignRow={setEditAssignRow}
            editAssignChars={editAssignChars} setEditAssignChars={setEditAssignChars}
            editAssignStdValues={editAssignStdValues} setEditAssignStdValues={setEditAssignStdValues}
            editAssignMode={editAssignMode} setEditAssignMode={setEditAssignMode}
            editAssignSaving={editAssignSaving} handleEditAssignSave={handleEditAssignSave}
          />
        )}

        {/* ═══════════ PRODUCT FORMAT TAB ═══════════ */}
        {tableView === "assignment" && (
          <InspectionProductFormatTab
            styles={styles} filterSelectStyles={filterSelectStyles} DeleteIcon={DeleteIcon} ddl={ddl} sbeeLogo={sbeeLogo}
            cascadedCableTypes={cascadedCableTypes} cascadedCodes={cascadedCodes}
            productFormatList={productFormatList} pfLoading={pfLoading}
            pfFilterSelections={pfFilterSelections} setPfFilterSelections={setPfFilterSelections} pfFilterOptions={pfFilterOptions}
            pfTemplateNoFilter={pfTemplateNoFilter} setPfTemplateNoFilter={setPfTemplateNoFilter}
            pfInspTestTypeFilter={pfInspTestTypeFilter} setPfInspTestTypeFilter={setPfInspTestTypeFilter}
            pfProductCodeFilter={pfProductCodeFilter} setPfProductCodeFilter={setPfProductCodeFilter}
            pfProductDescFilter={pfProductDescFilter} setPfProductDescFilter={setPfProductDescFilter}
            lookupProductByCode={lookupProductByCode} lookupProductByDescription={lookupProductByDescription}
            pfActiveFilterCount={pfActiveFilterCount}
            pfPage={pfPage} setPfPage={setPfPage} pfTotal={pfTotal} PF_PAGE_SIZE={PF_PAGE_SIZE}
            handleViewProductFormat={handleViewProductFormat} handleDeleteProductFormat={handleDeleteProductFormat}
            showPfModal={showPfModal} setShowPfModal={setShowPfModal} pfEditRow={pfEditRow}
            pfModalTab={pfModalTab} setPfModalTab={setPfModalTab}
            isStdValueEditMode={isStdValueEditMode} setIsStdValueEditMode={setIsStdValueEditMode}
            pfStdValues={pfStdValues} setPfStdValues={setPfStdValues} normChar={normChar}
            handleProductStdValueSave={handleProductStdValueSave}
            pfReportRef={pfReportRef}
            showPfViewModal={showPfViewModal} setShowPfViewModal={setShowPfViewModal}
          />
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />
    </Layout>
  );
}

/* ═══════════ STYLES ═══════════ */
const styles = {
  pageTitleBar:   { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 },
  pageTitle:      { fontSize:30, fontWeight:700, color:"#1e293b", marginBottom:2 },
  pageSubtitle:   { fontSize:12, color:"#64748b" },
  filterCard:     { background:"#ffffff", borderRadius:"12px", padding:"18px", boxShadow:"0 2px 8px rgba(15,23,42,0.06)", border:"1px solid #e2e8f0", marginBottom:"18px" },
  compactLabel:   { fontSize:"12px", fontWeight:600, marginBottom:"4px", color:"#334155", display:"block" },
  filterBar:      { display:"flex", alignItems:"flex-end", gap:8, flexWrap:"nowrap", overflowX:"auto", padding:"4px 0" },
  filterLabel:    { fontSize:11, fontWeight:600, color:"#64748b", whiteSpace:"nowrap", letterSpacing:"0.02em", paddingLeft:2 },
  clearBtn:       { border:"1px solid #cbd5e1", borderRadius:4, background:"#fff", fontSize:11, color:"#64748b", padding:"0 10px", height:30, cursor:"pointer", whiteSpace:"nowrap" },
  matchHint:      { marginTop:8, fontSize:12, color:"#475569" },
  tableContainer: { background:"#ffffff", borderRadius:8, padding:10, boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  toggleBar:      { display:"flex", gap:"0px", marginBottom:"12px", borderBottom:"1px solid #e2e8f0", paddingBottom:"10px" },
  toggleBtn:      { border:"none", borderRadius:"6px", padding:"6px 20px", fontSize:"13px", fontWeight:600, cursor:"pointer", transition:"all 0.15s" },
  toggleBtnActive:   { background:"#021843", color:"#ffffff" },
  toggleBtnInactive: { background:"#f1f5f9", color:"#64748b" },
  erpTable:       { width:"100%", borderCollapse:"collapse" },
  erpTh:          { background:"#f8fafc", fontSize:12, fontWeight:600, padding:"10px", borderBottom:"1px solid #e5e7eb", color:"#1e293b" },
  erpTd:          { fontSize:13, padding:"10px", borderBottom:"1px solid #f1f5f9", color:"#334155" },
  modalBackdrop:  { position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:9999, padding:"20px" },
  modal:          { width:"95%", maxWidth:"1280px", height:"92vh", background:"#ffffff", borderRadius:"16px", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 15px 40px rgba(0,0,0,0.18)" },
  modalHeader:    { padding:"18px 24px", borderBottom:"1px solid #e2e8f0", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#ffffff" },
  closeBtn:       { border:"none", background:"transparent", fontSize:"24px", color:"#64748b", cursor:"pointer" },
  modalBody:      { flex:1, overflowY:"auto", padding:"22px 24px", background:"#f8fafc" },
  modalFooter:    { padding:"16px 24px", borderTop:"1px solid #e2e8f0", display:"flex", justifyContent:"flex-end", background:"#ffffff" },
  selectedFormSection: { marginBottom:"18px" },
  simpleFieldRow:  { display:"flex", flexDirection:"column" },
  simpleFieldLabel: { fontSize:"11px", fontWeight:600, color:"#64748b", marginBottom:"4px", textTransform:"uppercase" },
  simpleFieldInput: { height:"32px", fontSize:"13px", background:"#fff" },
  saveBtn:         { background:"#021843", color:"#fff", border:"none", borderRadius:"8px", padding:"10px 26px", fontSize:"14px", fontWeight:600, cursor:"pointer" },
  characteristicsTable: { width:"100%", borderCollapse:"collapse", marginTop:"8px", background:"#fff" },
  tableHeader:     { fontSize:"13px", fontWeight:700, color:"#475569", textAlign:"left", padding:"10px 12px", borderBottom:"1px solid #e2e8f0", background:"#f8fafc" },
  tableCellCheckbox: { width:"50px", padding:"10px 12px", borderBottom:"1px solid #f1f5f9" },
  tableCell:       { fontSize:"14px", color:"#1e293b", padding:"10px 12px", borderBottom:"1px solid #f1f5f9" },
  tableCellUom:    { fontSize:"13px", color:"#64748b", padding:"10px 12px", borderBottom:"1px solid #f1f5f9", fontWeight:500 },
  simpleGroupWrapper: { marginBottom:"8px" },
  groupRow:        { padding:"4px 0" },
  groupLabel:      { display:"flex", alignItems:"center", gap:"10px", cursor:"pointer", margin:0 },
  simpleCharacteristicsSection: { paddingLeft:"28px", paddingTop:"4px", paddingBottom:"8px" },
  processText:     { fontSize:"15px", fontWeight:700, color:"#1e293b" },
  tabs:            { display:"flex", gap:"8px", marginBottom:"12px", marginTop:"8px" },
  compactTabBtn:   { fontSize:"13px", padding:"4px 14px" },
};

export default InspectionCharacteristics;